import React from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Slide,
} from "@mui/material";
import {
  CleaningServices,
  PestControl,
  LocalLaundryService,
  HomeRepairService,
  Groups,
  PriceCheck,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import Footer from "../components/Footer";
import Header from "../components/Header";

// Animation keyframes
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const PricingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const services = [
    {
      title: "Basic Cleaning Package",
      price: "600,000 UGX",
      originalPrice: "450,000 UGX",
      icon: <CleaningServices sx={{ fontSize: 40 }} />,
      description: "Essential cleaning services for your home or office",
      features: [
        "General surface cleaning",
        "Floor mopping & vacuuming",
        "Dusting & wiping surfaces",
        "Bathroom sanitization",
        "Kitcleaning",
      ],
      note: "Final price determined after site visit",
      color: "#4CAF50",
      popular: false,
    },
    {
      title: "Standard Cleaning Package",
      price: "750,000 UGX",
      originalPrice: "900,000 UGX",
      icon: <HomeRepairService sx={{ fontSize: 40 }} />,
      description: "Comprehensive cleaning with extra attention to detail",
      features: [
        "Everything in Basic Package",
        "Deep kitchen cleaning",
        "Window cleaning (interior)",
        "Furniture polishing",
        "Appliance exterior cleaning",
        "Organizational assistance",
      ],
      note: "Prices may vary after site assessment",
      color: "#2196F3",
      popular: true,
    },
    {
      title: "Monthly Maid Contract",
      price: "350,000 UGX",
      icon: <Groups sx={{ fontSize: 40 }} />,
      description: "Regular cleaning service on a monthly contract basis",
      features: [
        "Weekly cleaning sessions",
        "Schedule flexibility",
        "Dedicated cleaning professional",
        "Consistent quality",
        "Priority scheduling",
        "Discount on additional services",
      ],
      note: "Per month contract",
      color: "#9C27B0",
      popular: false,
    },
    {
      title: "Laundry Service",
      price: "70,000 UGX",
      icon: <LocalLaundryService sx={{ fontSize: 40 }} />,
      description: "Professional laundry cleaning from your home",
      features: [
        "Pick-up and delivery",
        "Washing & drying",
        "Ironing & folding",
        "Stain treatment",
        "Fragrance options",
        "Same-day service available",
      ],
      note: "Per day service rate",
      color: "#FF9800",
      popular: false,
    },
    {
      title: "Fumigation Services",
      price: "Site Visit Required",
      icon: <PestControl sx={{ fontSize: 40 }} />,
      description: "Professional pest control and fumigation",
      features: [
        "Complete pest assessment",
        "Safe chemical application",
        "Rodent control",
        "Insect elimination",
        "Follow-up treatment",
        "Eco-friendly options",
      ],
      note: "Custom pricing after site evaluation",
      color: "#F44336",
      popular: false,
    },
    {
      title: "Deep Cleaning",
      price: "Custom Quote",
      icon: <PriceCheck sx={{ fontSize: 40 }} />,
      description: "Intensive cleaning for special occasions or moving",
      features: [
        "Complete interior deep clean",
        "Grout and tile cleaning",
        "Inside appliances",
        "Blinds and light fixtures",
        "Baseboards and trim",
        "Upholstery cleaning available",
      ],
      note: "Requires site visit for pricing",
      color: "#607D8B",
      popular: false,
    },
  ];

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Slide in direction="down" timeout={500}>
          <Box textAlign="center" mb={6}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: `${floatAnimation} 3s ease-in-out infinite`,
              }}
            >
              Our Services & Pricing
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
              Transparent pricing with professional quality guaranteed
            </Typography>
            <Chip
              label="All final prices are confirmed after site visit"
              color="warning"
              icon={<PriceCheck />}
              sx={{ fontSize: "1rem", py: 2 }}
            />
          </Box>
        </Slide>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Fade
                in
                timeout={800}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <Box>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "all 0.3s ease",
                      border: `2px solid ${service.color}20`,
                      borderRadius: 3,
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: `0 20px 40px ${service.color}30`,
                        animation: `${pulseAnimation} 2s ease-in-out`,
                      },
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    {service.popular && (
                      <Zoom in>
                        <Chip
                          label="MOST POPULAR"
                          color="primary"
                          sx={{
                            position: "absolute",
                            top: -12,
                            right: 20,
                            fontWeight: "bold",
                            fontSize: "0.75rem",
                          }}
                        />
                      </Zoom>
                    )}

                    {service.originalPrice && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: -10,
                          bgcolor: "error.main",
                          color: "white",
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          transform: "rotate(-15deg)",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        WAS {service.originalPrice}
                      </Box>
                    )}

                    <CardHeader
                      avatar={
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: `${service.color}15`,
                            color: service.color,
                          }}
                        >
                          {service.icon}
                        </Box>
                      }
                      title={
                        <Typography
                          variant="h5"
                          component="h2"
                          fontWeight={600}
                        >
                          {service.title}
                        </Typography>
                      }
                      subheader={
                        <Typography variant="body2" color="text.secondary">
                          {service.description}
                        </Typography>
                      }
                    />

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box
                        sx={{
                          textAlign: "center",
                          mb: 3,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: `${service.color}10`,
                        }}
                      >
                        <Typography
                          variant="h3"
                          component="div"
                          fontWeight={700}
                        >
                          {service.price}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, fontStyle: "italic" }}
                        >
                          {service.note}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }}>
                        <Chip label="Includes" size="small" />
                      </Divider>

                      <Box sx={{ mb: 3 }}>
                        {service.features.map((feature, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: service.color,
                                mr: 2,
                              }}
                            />
                            <Typography variant="body2">{feature}</Typography>
                          </Box>
                        ))}
                      </Box>

                      <Box
                        sx={{
                          mt: "auto",
                          pt: 2,
                          borderTop: `1px dashed ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <PriceCheck fontSize="small" />
                          Book a site visit for exact pricing
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {/* Additional Info Section */}
        <Zoom in timeout={1000}>
          <Box
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 3,
              bgcolor: "primary.light",
              background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
              border: `1px solid ${theme.palette.primary.light}30`,
            }}
          >
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" gutterBottom fontWeight={600}>
                  Why Site Visits Are Required
                </Typography>
                <Typography variant="body1" paragraph>
                  We conduct thorough site assessments to provide you with
                  accurate pricing because every space is unique. Our
                  professionals evaluate:
                </Typography>
                <Grid container spacing={2}>
                  {[
                    "Space size and layout complexity",
                    "Specific cleaning requirements",
                    "Special surfaces or materials",
                    "Accessibility considerations",
                    "Current condition assessment",
                  ].map((item, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CleaningServices color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    textAlign: "center",
                    boxShadow: theme.shadows[3],
                  }}
                >
                  <PriceCheck
                    sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    Get Exact Pricing
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact us today to schedule your free, no-obligation site
                    visit and receive your personalized quote.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Zoom>
      </Container>
      <Footer />
    </>
  );
};

export default PricingPage;
